import { useEffect, useRef, useState } from "react";
import { KeyRound, LogIn, LogOut, Loader2, CheckCircle2, Baby } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ClientPinDialog } from "@/components/care/ClientPinDialog";

/** Kiosk tile: parent enters family PIN → taps each child to sign in / out. */
export function ParentPinSignin() {
  const [pinOpen, setPinOpen] = useState(false);
  const [session, setSession] = useState(null); // {pin, phone_last4, parent_name, children}
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState([]);
  const [closingIn, setClosingIn] = useState(0);
  const timer = useRef(null);

  const close = () => { clearTimeout(timer.current); setClosingIn(0); setSession(null); };
  useEffect(() => () => clearTimeout(timer.current), []);

  const act = async (child) => {
    const event_type = child.status === "in" ? "pickup" : "drop_off";
    setBusy(child.id);
    try {
      await api.post("/daycare/kiosk/event", { pin: session.pin, phone_last4: session.phone_last4 || "", student_id: child.id, event_type });
      const children = session.children.map((c) => (c.id === child.id ? { ...c, status: event_type === "drop_off" ? "in" : "out" } : c));
      const msg = `${child.full_name} ${event_type === "drop_off" ? "signed in" : "signed out"}`;
      toast.success(msg, { description: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }), duration: 3500 });
      const remaining = children.filter((c) => c.id !== child.id && c.status === child.status);
      if (remaining.length === 0) { close(); return; }   // nothing else to do for this family → back to the welcome screen
      setSession((s) => ({ ...s, children }));
      setDone((d) => [...d, msg]);
      clearTimeout(timer.current);
      setClosingIn(6);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not record"); }
    finally { setBusy(""); }
  };

  useEffect(() => {   // short countdown so a parent with several children can still tap the next one
    if (!closingIn) return undefined;
    if (closingIn === 1) { timer.current = setTimeout(close, 1000); return () => clearTimeout(timer.current); }
    timer.current = setTimeout(() => setClosingIn((n) => n - 1), 1000);
    return () => clearTimeout(timer.current);
  }, [closingIn]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <button type="button" onClick={() => setPinOpen(true)} data-testid="daycare-parent-signin-tile"
        className="group flex w-full items-center gap-6 rounded-3xl bg-primary px-8 py-8 text-left text-white shadow-lg transition hover:shadow-2xl active:scale-[0.99]">
        <span className="h-16 w-16 shrink-0 rounded-2xl bg-white/15 inline-flex items-center justify-center"><KeyRound className="h-8 w-8" /></span>
        <span className="flex-1">
          <span className="block font-display text-3xl font-semibold tracking-tight">Parents: sign in / sign out</span>
          <span className="block text-sm uppercase tracking-widest opacity-80 mt-1">Tap, enter your family PIN, then tap your child</span>
        </span>
      </button>

      <ClientPinDialog open={pinOpen} onClose={() => setPinOpen(false)} identifyPath="/daycare/kiosk/identify" testid="parent-pin-dialog"
        title="Family PIN" description="Enter the 4-digit PIN from your parent app"
        onIdentified={(data, pin, extra) => { setPinOpen(false); setDone([]); setClosingIn(0); setSession({ ...data, pin, phone_last4: extra?.phone_last4 || "" }); }} />

      <Dialog open={!!session} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-md rounded-3xl p-6" data-testid="parent-children-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Hi {session?.parent_name?.split(" ")[0] || "there"} 👋</DialogTitle>
            <DialogDescription>Tap a child to sign them {session?.children?.some((c) => c.status === "out") ? "in" : "out"}.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 my-2">
            {(session?.children || []).map((c) => (
              <button key={c.id} onClick={() => act(c)} disabled={!!busy} data-testid={`kiosk-child-${c.id}`}
                className={`flex items-center gap-3 rounded-2xl border-2 p-4 text-left transition active:scale-[0.98] ${c.status === "in" ? "border-slate-300 bg-slate-50" : "border-emerald-300 bg-emerald-50"}`}>
                {c.photo_data_url ? <img src={c.photo_data_url} alt="" className="h-12 w-12 rounded-full object-cover" /> : <span className="h-12 w-12 rounded-full bg-white inline-flex items-center justify-center text-slate-500"><Baby className="h-6 w-6" /></span>}
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-slate-900 truncate">{c.full_name}</span>
                  <span className="block text-xs text-slate-500">{c.room || "No room"} · {c.status === "in" ? "Here since this morning" : "Not signed in"}</span>
                </span>
                <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${c.status === "in" ? "bg-slate-900 text-white" : "bg-emerald-600 text-white"}`}>
                  {busy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : c.status === "in" ? <><LogOut className="h-3.5 w-3.5" /> Sign out</> : <><LogIn className="h-3.5 w-3.5" /> Sign in</>}
                </span>
              </button>
            ))}
          </div>
          {done.length > 0 && <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="kiosk-done-msg"><CheckCircle2 className="h-4 w-4" /> {done.join(" · ")}</div>}
          <button onClick={close} className="mt-2 h-11 w-full rounded-xl bg-slate-900 text-sm font-semibold text-white" data-testid="kiosk-parent-done">{closingIn ? `Done · closing in ${closingIn}s` : "Done"}</button>
        </DialogContent>
      </Dialog>
    </>
  );
}
